import { Snackbar } from '@material-ui/core';
import Box from '@mui/material/Box';
import lodash from 'lodash';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ConversationType } from '../../../enums/chat';
import { useAppDispatch, useWorkCheckpointWebSocket } from '../../../hooks';
import useIntervalCallback from '../../../pages/Chat/hooks/useIntervalCallback';
import { createNewConversation, fetchUserConversationState, fetchUserPermissions, openChat, useConversations } from '../../../reducers/chat';
import { UserSelectors } from '../../../reducers/user';
import { activateWork, cancelWork, checkActiveWork, checkpointWork, clearWorkActionError, loadAvailableWork, setWorkActionError, setWorkRatings, skipWork, submitWorkFeedback, submitWorkRating, submitWorkSolution, WorkSelectors } from '../../../reducers/work';
import { pollAsyncJob } from '../../../services/asyncJob';
import { signedIn } from '../../../services/auth';
import { durationSecondsAsString } from '../../../services/utils';
import { attachmentsStorage, clearAllWorkStorage, inactivityPingStorage, prListStorage, reviewStatusStorage, workflowStatusStorage } from '../../../services/workStorage';
import { Rating, SelectModal } from '../../../shared';
import WorkDialog from '../../../shared/dialog';
import { AttachmentType } from '../../../types/attachments';
import { CONVERSATION_PAGE_SIZE, FETCH_CONVERSATION_INTERVAL } from '../../../types/chat';
import { ContributorWorkSteps } from '../../../types/contributorWork';
import { isRatingsCompleted, RatingSubject, UserRatings } from '../../../types/rating';
import { TaskStatus } from '../../../types/task';
import { isCodingWorkItem, isFollowupWorkItem, isPlanningQuestionsAnsweringWorkItem, isQuestReviewWorkItem, isResolveConflictsWorkItem, isReviewRequestRejectionWorkItem, WorkItem, workMaxDurationMs, WorkReviewStatus, WorkStatus, WorkType } from '../../../types/work';
import { options } from '../../../types/workTypes';
import { generateEntityId } from '../../../utils/chat';
import { isUserOffline } from '../../../utils/userStatus';
import AddPullRequest from './AddPullRequest';
import ApproveDialog from './ApproveDialog';
import AttachmentUpload from './AttachmentUpload';
import BlockerDialog from './BlockerDialog';
import Description from './Description';
import Dialogs from './dialogs';
import FeedbackHistory from './FeedbackHistory';
import FooterButtons from './FooterButtons';
import Header from './Header';
import ImplementationPlan from './ImplementationPlan';
import OfflineView, { OFFLINE_USER_MESSAGE } from './offlineView';
import PlanningQuestionsAnswering from './PlanningQuestionsAnswering';
import PlanningSpinner from './PlanningQuestionsAnswering/PlanningSpinner';
import QuestReviewDialog from './QuestReviewDialog';
import QuestReviewProofOfWork, { CompletedIterationData, CompletedTaskWithAttachments } from './QuestReviewProofOfWork';
import SolutionAttachments from './SolutionAttachments';
import { BG, Container, DescriptionBox, DescriptionTermsContainer, DescriptionWrapper } from './styled';
import TaskCompleted from './TaskCompleted';
import Terms from './Terms';
import WorkflowStatus from './WorkflowStatus';

type Props = {
    work: WorkItem;
    openConversation?: boolean;
};

const ContributorWork: FunctionComponent<Props> = ({ work, openConversation }: Props) => {
    const dispatch = useAppDispatch();
    const { profile } = UserSelectors();
    const { workRatings, workStartTimeEpochMs, workActive, activeWorkRecord, workLoading, workActionError, workRatingAuthorizationCode, workToken, workDone } = WorkSelectors();
    const [ workRatingSubmitted, setWorkRatingSubmitted ] = useState<boolean>(false);
    const [ currentStep, setCurrentStep ] = useState<ContributorWorkSteps>(workActive ? ContributorWorkSteps.WorkingOnPullRequest : ContributorWorkSteps.TaskAccept);
    // True while the backend auto-resolves merge conflicts on the just-submitted
    // PR in the background (see handleSubmitWorkSolution). Keeps the Done screen
    // in a "resolving" state until the async job finishes.
    const [ isResolvingConflicts, setIsResolvingConflicts ] = useState(false);
    const [ solutionUrls, setSolutionUrls ] = useState<string[]>([]);
    const [ feedbackDialogOpen, setFeedbackDialogOpen ] = useState(false);
    const [ cancellationDialogOpen, setCancellationDialogOpen ] = useState(false);
    const [ feedbackAdded, setFeedbackAdded ] = useState<boolean>(false);
    const [ isVisible, setIsVisible ] = useState<boolean>(false);
    const [ unavailableWorkDialog, setUnavailableWorkDialog ] = useState<boolean>(false);
    const [ questReviewFeedback, setQuestReviewFeedback ] = useState<string>('');
    const [ questReviewDialogOpen, setQuestReviewDialogOpen ] = useState<boolean>(false);
    const [ approveDialogOpen, setApproveDialogOpen ] = useState<boolean>(false);
    const [ approveAttachmentIds, setApproveAttachmentIds ] = useState<number[]>([]);
    const [ attachmentIds, setAttachmentIds ] = useState<number[]>([]);
    const [ isAttachmentUploading, setIsAttachmentUploading ] = useState<boolean>(false);
    const [ blockerDialogOpen, setBlockerDialogOpen ] = useState<boolean>(false);
    const [ blockerDescription, setBlockerDescription ] = useState<string>('');
    const [ inactivityPingDialogOpen, setInactivityPingDialogOpen ] = useState<boolean>(false);
    const [ inactivityPingNumber, setInactivityPingNumber ] = useState<number>(1);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const prevWsConnectedRef = useRef<boolean | null>(null);
    const [ currentTime, setCurrentTime ] = useState<number>(Date.now());
    const [ wsConnected, setWsConnected ] = useState<boolean | null>(null); // null = not initialized yet
    const [ workflowsPassed, setWorkflowsPassed ] = useState<boolean | undefined>(undefined);
    const [ workflowsFeedback, setWorkflowsFeedback ] = useState<any>(undefined);
    const [ isAnalyzingWorkflows, setIsAnalyzingWorkflows ] = useState<boolean>(false);
    const conversations = useConversations();

    const history = useHistory();

    // Extract completed tasks with attachments for Quest Review work type
    const questReviewCompletedTasks = useMemo(() => {
        const completedTasks = work.workInput?.['completed_tasks_by_iteration'];
        if (!completedTasks) {
            return [];
        }
        return Object.keys(completedTasks)
            .map(Number)
            .map((iterationNumber): CompletedIterationData => ({
                iterationNumber,
                tasks: completedTasks[iterationNumber] as CompletedTaskWithAttachments[]
            }))
            .sort((a, b) => b.iterationNumber - a.iterationNumber);
    }, [ work.workType, work.workInput ]);
    
    // Find work conversation by entityId
    const workEntityId = useMemo(() => generateEntityId(ConversationType.WORK, work.id.toString()), [ work.id ]);
    const workConversation = useMemo(() => {
        return Object.values(conversations).find(
            (conv) => conv.entityId === workEntityId && conv.conversationId > 0
        );
    }, [ conversations, workEntityId ]);
    
    // Check if work conversation has unread messages
    const hasUnreadMessages = useMemo(() => {
        return workConversation ? (workConversation.unreadMessageCount ?? 0) > 0 : false;
    }, [ workConversation ]);
    
    // Memoized filtered attachments to avoid re-filtering on every render
    const solutionAttachments = useMemo(() => {
        return work.taskAttachments?.filter(attachment => attachment.step === AttachmentType.SOLUTION) || [];
    }, [ work.taskAttachments ]);
    
    const delegationAttachments = useMemo(() => {
        return work.taskAttachments?.filter(attachment => attachment.step === AttachmentType.DELEGATION) || [];
    }, [ work.taskAttachments ]);

    const hasAssociatedBlocker = useMemo(() => {
        return !!work.workInput?.['associated_blocker_task_title'];
    }, [ work.workInput ]);

    const workflowsRequired = useMemo(() => {
        return !!work.workInput?.['required_workflows'];
    }, [ work.workInput ]);

    const blockerTaskAttachments = useMemo(() => {
        if (!hasAssociatedBlocker) {
            return [];
        }
        const attachments = work.workInput?.['associated_blocker_task_delegation_attachments'];
        if (Array.isArray(attachments)) {
            return attachments.map((att: any) => ({
                id: att.id,
                name: att.name,
                type: att.type,
                step: att.step
            }));
        }
        return [];
    }, [ work.workInput, hasAssociatedBlocker ]);

    const hasFeedbackHistory = useMemo(() => {
        return Array.isArray(work.workInput?.['quest_feedback_history']) &&
        work.workInput['quest_feedback_history'].length > 0;
    }, [ work.workInput ]);
    const isBaseFeedbackMode = useMemo(() => {
        return isReviewRequestRejectionWorkItem(work.workType) &&
        hasFeedbackHistory;
    }, [ work.workType, hasFeedbackHistory ]);
    const isRequestReviewFeedbackMode = useMemo(() => {
        return isBaseFeedbackMode || 
        (isCodingWorkItem(work.workType) && hasFeedbackHistory);
    }, [ work.workType, hasFeedbackHistory ]);
    const isQuestFeedbackHistoryMode = useMemo(() => {
        return isBaseFeedbackMode || 
        ((isQuestReviewWorkItem(work.workType) || isCodingWorkItem(work.workType)) && hasFeedbackHistory);
    }, [ work.workType, hasFeedbackHistory ]);

    const originalQuestAttachments = useMemo(() => {
        const attachments = work.workInput?.['original_quest_attachments'];
        if (Array.isArray(attachments)) {
            return attachments.map((att: any) => ({
                id: att.id,
                name: att.name,
                type: att.type,
                step: att.step
            }));
        }
        return [];
    }, [ work.workInput ]);

    const clientReviewNotes = useMemo(() => {
        return work.workInput?.['client_notes'] || '';
    }, [ work.workInput ]);

    const clientReviewAttachments = useMemo(() => {
        const attachments = work.workInput?.['client_attachments'];
        if (Array.isArray(attachments)) {
            return attachments.map((att: any) => ({
                id: att.id,
                name: att.name,
                type: att.type,
                step: att.step
            }));
        }
        return [];
    }, [ work.workInput ]);

    const hasClientReviewNotes = useMemo(() => {
        return Boolean(clientReviewNotes) || clientReviewAttachments.length > 0;
    }, [ clientReviewNotes, clientReviewAttachments ]);

    const timeInProcess = useMemo(
        () => currentTime - workStartTimeEpochMs,
        [ currentTime, workStartTimeEpochMs ]
    );

    const targetDate = useMemo(
        () => new Date((workStartTimeEpochMs || Date.now()) + workMaxDurationMs - (new Date().getTimezoneOffset() * 60000)).toISOString().slice(0, -8),
        [ workMaxDurationMs, workStartTimeEpochMs ]
    );

    const timeRemaining = useMemo(
        () => durationSecondsAsString(Math.max((workMaxDurationMs - timeInProcess) / 1000, 0)),
        [ workMaxDurationMs, timeInProcess ]
    );

    // Helper to check if work type doesn't require pull requests
    const isNonPullRequestWorkType = useMemo(() => {
        return isQuestReviewWorkItem(work.workType) ||
               isReviewRequestRejectionWorkItem(work.workType);
    }, [ work.workType ]);

    // Determine if pull request section should be shown
    const shouldShowPullRequestSection = useMemo(() => {
        return (currentStep === ContributorWorkSteps.WorkingOnPullRequest && !isNonPullRequestWorkType) ||
               isResolveConflictsWorkItem(work.workType);
    }, [ currentStep, isNonPullRequestWorkType, work.workType ]);

    // Determine if attachments should be shown
    const shouldShowAttachmentsSection = useMemo(() => {
        return solutionUrls?.length > 0 && currentStep === ContributorWorkSteps.WorkingOnPullRequest;
    }, [ solutionUrls, currentStep ]);

    useEffect(() => {
        setUnavailableWorkDialog(work?.status === WorkStatus.UNAVAILABLE && work?.task?.status === TaskStatus.ON_HOLD);
    }, [ work ]);

    useEffect(() => {
        if (unavailableWorkDialog && work?.id) {
            setInactivityPingDialogOpen(false);
            inactivityPingStorage.clear(work.id);
        }
    }, [ unavailableWorkDialog, work?.id ]);

    const checkWorkAvailability = useCallback(() => {
        if (work?.id && workActive && !workDone && !workLoading) {
            dispatch(checkActiveWork())
                .unwrap()
                .then((response) => {
                    if (!response || !response.workId || Number(response.workId) !== work.id) {
                        setUnavailableWorkDialog(true);
                    }
                })
                .catch((error) => {
                    const errorStatus = error && error.status;
                    // 404 means no active work - MUST show the dialog since work is no longer available
                    if (errorStatus === 404) {
                        setUnavailableWorkDialog(true);
                    }
                });
        }
    }, [ work?.id, workActive, workDone, workLoading, dispatch ]);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            checkWorkAvailability();
        }, 60000); // 60000ms = 1 minute

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [ checkWorkAvailability ]);

    useEffect(() => {
        setCurrentTime(Date.now());

        // update the formatted time every 60 seconds
        const updateInterval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 60000);

        return () => {
            clearInterval(updateInterval);
        };
    }, []);

    const handleClose = () => {
        setIsVisible(false);
    };

    // Load review status from storage when work changes
    useEffect(() => {
        if (!work?.id) {
            return;
        }
        const savedReviewStatus = reviewStatusStorage.load(work.id);
        setQuestReviewFeedback(savedReviewStatus.questReviewFeedback);
        setApproveDialogOpen(savedReviewStatus.approveDialogOpen);
        setQuestReviewDialogOpen(savedReviewStatus.questReviewDialogOpen);
    }, [ work?.id ]);

    // Load workflow status from storage when work changes
    useEffect(() => {
        if (!work?.id) {
            return;
        }
        const savedWorkflowStatus = workflowStatusStorage.load(work.id);
        if (savedWorkflowStatus) {
            setWorkflowsPassed(savedWorkflowStatus.workflowsPassed);
            setWorkflowsFeedback(savedWorkflowStatus.workflowsFeedback);
            // Don't restore analyzing state - it's ephemeral
        }
    }, [ work?.id ]);

    // Load inactivity ping state from storage when work changes
    useEffect(() => {
        if (!work?.id) {
            return;
        }
        const savedPing = inactivityPingStorage.load(work.id);
        setInactivityPingDialogOpen(savedPing.pingDialogOpen);
        setInactivityPingNumber(savedPing.pingNumber);
    }, [ work?.id ]);

    // Load PR list from storage when work changes
    useEffect(() => {
        if (!work?.id) {
            return;
        }
        const savedPrList = prListStorage.load(work.id);
        if (savedPrList && savedPrList.length > 0) {
            setSolutionUrls(savedPrList);
        }
    }, [ work?.id ]);

    // Handle WebSocket reconnection - clear analyzing state if it was stuck.
    useEffect(() => {
        const wasDisconnected = prevWsConnectedRef.current === false;
        prevWsConnectedRef.current = wsConnected;

        if (wsConnected === true && wasDisconnected && isAnalyzingWorkflows) {
            setIsAnalyzingWorkflows(false);
            if (work?.id) {
                workflowStatusStorage.save(work.id, {
                    workflowsPassed,
                    workflowsFeedback,
                    isAnalyzingWorkflows: false
                });
            }
        }
    }, [ wsConnected, isAnalyzingWorkflows, work?.id, workflowsPassed, workflowsFeedback ]);

    const pollUserPermissions = useCallback(() => {
        if (!signedIn()) {
            return Promise.resolve(undefined);
        }
        return dispatch(fetchUserPermissions())
            .unwrap()
            .catch(() => undefined);
    }, [ dispatch ]);

    useEffect(() => {
        pollUserPermissions();
    }, [ pollUserPermissions ]);

    useIntervalCallback(pollUserPermissions, FETCH_CONVERSATION_INTERVAL);

    const getVariant = () => {
        if (workActive) {
            return {
                type: 'Secondary',
                context: work.context,
                projectName: work.projectName || '',
                projectDescription: work.projectDescription || '',
                projectAttachments: work.projectAttachments || [],
                isSeniorWork: work.isSeniorWork
            };
        }
        const workTypeClassification = work.workType === WorkType.CODING ? work.taskType : lodash.startCase(lodash.toLower(WorkType[work.workType]));
        return {
            type: 'Primary',
            workType: workTypeClassification || 'Uncertain',
            skillsNeeded: work.skills,
            context: work.context,
            projectName: work.projectName || '',
            projectDescription: work.projectDescription || '',
            projectAttachments: work.projectAttachments || [],
            isSeniorWork: work.isSeniorWork,
            onAccept: handleActivateWork,
            onSkip: handleSkipWork
        };
    };

    const getPrimaryText = () => {
        if (isReviewRequestRejectionWorkItem(work.workType) && currentStep !== ContributorWorkSteps.TaskAccept) {
            return 'Back to breakdown';
        }
        if (isFollowupWorkItem(work.workType) && currentStep !== ContributorWorkSteps.TaskAccept) {
            if (isResolveConflictsWorkItem(work.workType)) {
                return '';
            }
            return 'Request Modifications';
        }
        return '';
    };

    const getPrimaryDisabled = () => {
        if (workLoading) {
            return true;
        }
        if (isReviewRequestRejectionWorkItem(work.workType)) {
            return false; // Back to breakdown is always enabled
        }
        if (isResolveConflictsWorkItem(work.workType)) {
            return false; // No primary button for resolve conflicts
        }
        if (isQuestReviewWorkItem(work.workType)) {
            return false;
        }
        if (currentStep === ContributorWorkSteps.DescriptionFeedback) {
            if (feedbackAdded) {
                return false;
            }
            return true;
        }
        return false;
    };

    const getSecondaryDisabled = () => {
        if (workLoading) {
            return true;
        }
        
        // If workflows are required and we're analyzing, disable submit
        if (workflowsRequired && isAnalyzingWorkflows) {
            return true;
        }
        
        if (isReviewRequestRejectionWorkItem(work.workType)) {
            return false; // Fix by yourself is always enabled
        }
        if (isResolveConflictsWorkItem(work.workType)) {
            return !solutionUrls || solutionUrls.length === 0;
        }
        if (isQuestReviewWorkItem(work.workType)) {
            return false;
        }
        if (currentStep === ContributorWorkSteps.WorkingOnPullRequest) {
            const requireAttachments = isCodingWorkItem(work.workType) || isQuestReviewWorkItem(work.workType);
            return !solutionUrls || solutionUrls.length === 0 || (requireAttachments && (attachmentIds.length === 0 || isAttachmentUploading));
        }
        return false;
    };

    const getSecondaryText = () => {
        if (isReviewRequestRejectionWorkItem(work.workType) && currentStep !== ContributorWorkSteps.TaskAccept) {
            return 'Fix by yourself';
        }
        if (isFollowupWorkItem(work.workType) && currentStep !== ContributorWorkSteps.TaskAccept) {
            if (isResolveConflictsWorkItem(work.workType)) {
                return 'Submit';
            }
            return 'Approve';
        }
        if (currentStep === ContributorWorkSteps.WorkingOnPullRequest || currentStep === ContributorWorkSteps.SubmitPullRequest) {
            return 'Submit';
        }
        return '';
    };

    const handlePrimaryClick = () => {
        if (isReviewRequestRejectionWorkItem(work.workType)) {
            handleReviewRequestRejectionBackToBreakdown();
            return;
        }
        if (isQuestReviewWorkItem(work.workType)) {
            // Dialog opens only via WebSocket when review status is received
            return;
        }
    };

    const handleSecondaryClick = () => {
        if (isReviewRequestRejectionWorkItem(work.workType)) {
            handleReviewRequestRejectionFixByYourself();
            return;
        }
        if (isFollowupWorkItem(work.workType)) {
            if (isQuestReviewWorkItem(work.workType)) {
                // Dialog opens only via WebSocket when review status is received
                return;
            }
            handleSubmitWorkSolution(WorkReviewStatus.ADEQUATE, undefined, solutionUrls, attachmentIds);
            return;
        } else if (currentStep === ContributorWorkSteps.WorkingOnPullRequest) {
            handleSubmitWorkSolution(undefined, undefined, solutionUrls, attachmentIds);
        }
    };

    const handleSelectWorkTypeSubmit = () => {
        handleClose();
    };

    const _calculateDurationSeconds = () => {
        return Math.round((Date.now() - workStartTimeEpochMs) / 1000);
    };

    const formatErrorMessage = (error) => {
        switch (error) {
            case 'package_builtin':
                return 'This is a built in package. You can import it without installation.';
            case 'rating':
                return 'Error while submitting rating. Please try again';
            case 'rating_invalid':
                return 'Please fill in a rating score between 1-5.';
            case 'rating_submission_failed':
                return 'Failed to submit ratings. Your work solution will still be submitted.';
            case 'qa_toggle':
                return 'Please choose further instructions in toggle button.';
            case 'conflicts_not_resolved':
                return 'There are still conflicts in the Pull Request. Please resolve them before submitting.';
            case 'user_offline':
                return OFFLINE_USER_MESSAGE;
            default:
                return 'Unknown error. Please try again';
        }
    };

    const getTaskDescriptionTitle = (workType: WorkType) => {
        switch (workType) {
            case WorkType.RESOLVE_CONFLICTS:
                return 'Original task description';
            case WorkType.QUEST_REVIEW:
            case WorkType.REVIEW_REQUEST_REJECTION:
                return 'Original quest description';
            default:
                return 'Task description';
        }
    };

    const handleSubmitWorkFeedback = useCallback(
        (feedback: string) => {
            dispatch(
                submitWorkFeedback({
                    workId: work.id,
                    feedback,
                    durationSeconds: _calculateDurationSeconds()
                })
            ).unwrap().then(() => {
                setFeedbackDialogOpen(false);
                dispatch(loadAvailableWork({ currentWorkId: work.id }));
            });

        },
        [ dispatch, submitWorkFeedback, work, _calculateDurationSeconds ]
    );

    const handleSkipWork = useCallback(
        () => {
            const startTimeEpochMs = Date.now();
            const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;

            dispatch(skipWork({
                workId: work.id,
                startTimeEpochMs,
                tzName
            })).unwrap().then(() => {
                clearAllWorkStorage(work.id);
                dispatch(loadAvailableWork({ currentWorkId: work.id }));
                setFeedbackAdded(false);
            });
        },
        [ dispatch, loadAvailableWork, work ]
    );

    const handleActivateWork = useCallback(
        () => {
            const workStartTimeEpochMs = Date.now();
            const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;

            dispatch(
                activateWork({
                    workId: work.id,
                    workType: work.workType,
                    startTimeEpochMs: workStartTimeEpochMs,
                    tzName
                })
            ).unwrap().then((result) => {
                if (!result['error']) {
                    setCurrentStep(ContributorWorkSteps.WorkingOnPullRequest);
                }
            });
        },
        [ activateWork, dispatch, work ]
    );

    // Effect to clear review state when work changes or when a new work session starts
    // This handles: switching work items, or same work.id but different session (e.g., User A cancels, User B activates)
    useEffect(() => {
        setQuestReviewFeedback('');
        setQuestReviewDialogOpen(false);
        setApproveDialogOpen(false);
        setApproveAttachmentIds([]);
        setWorkflowsPassed(undefined);
        setWorkflowsFeedback(undefined);
        setIsAnalyzingWorkflows(false);

        // Clear workflow status from storage when work changes
        workflowStatusStorage.clear(work.id);
    }, [ work.id, activeWorkRecord?.id ]);

    // Callback to handle solution URLs updates from WebSocket
    const handleSolutionUrlsUpdate = useCallback((newUrls: string[]) => {
        setSolutionUrls(prevUrls => {
            // Merge new URLs with existing ones, avoiding duplicates
            const uniqueNewUrls = newUrls.filter(url => !prevUrls.includes(url));
            return uniqueNewUrls.length > 0 ? [ ...prevUrls, ...uniqueNewUrls ] : prevUrls;
        });
        
        // If workflows are required and we just received URLs, start analyzing
        if (workflowsRequired) {
            setIsAnalyzingWorkflows(true);
            // Save analyzing state to storage
            if (work?.id) {
                workflowStatusStorage.save(work.id, {
                    workflowsPassed,
                    workflowsFeedback,
                    isAnalyzingWorkflows: true
                });
            }
        }
    }, [ workflowsRequired, work?.id, workflowsPassed, workflowsFeedback ]);

    // Callback to handle review status updates from WebSocket
    // ADEQUATE: Opens dialog for user to add attachments and approve
    // INADEQUATE:
    //   - Quest Review: Opens dialog to choose between "back to breakdown" or "fix by yourself"
    //   - Community Review: Enables solve mode to show PR list and submit buttons
    const handleReviewStatusUpdate = useCallback((reviewStatus: number, rejectionReason?: string) => {
        if (reviewStatus === WorkReviewStatus.ADEQUATE) {
            // Open approve dialog for ADEQUATE status
            if (isQuestReviewWorkItem(work.workType)) {
                setApproveAttachmentIds([]);
                attachmentsStorage.clear(work.id);
            }
            setApproveDialogOpen(true);
            // Save to storage - only approve dialog state
            reviewStatusStorage.save(work.id, {
                approveDialogOpen: true
            });
        } else if (reviewStatus === WorkReviewStatus.INADEQUATE) {
            if (isQuestReviewWorkItem(work.workType)) {
                setQuestReviewFeedback(rejectionReason || '');
                setQuestReviewDialogOpen(true);
                // Save to storage - only quest review data
                reviewStatusStorage.save(work.id, {
                    questReviewFeedback: rejectionReason || '',
                    questReviewDialogOpen: true
                });
            }
        }
    }, [ work.workType, work.id ]);

    // Callback to handle workflows update from WebSocket
    const handleWorkflowsUpdate = useCallback((passed: boolean, feedback?: any) => {
        setWorkflowsPassed(passed);
        setWorkflowsFeedback(feedback);
        setIsAnalyzingWorkflows(false);
        
        // Save to storage immediately after WebSocket update
        if (work?.id) {
            workflowStatusStorage.save(work.id, {
                workflowsPassed: passed,
                workflowsFeedback: feedback,
                isAnalyzingWorkflows: false
            });
        }
    }, [ work?.id ]);

    // Callback to handle WebSocket connection status changes
    const handleConnectionStatusChange = useCallback((connected: boolean) => {
        setWsConnected(connected);
    }, []);

    const handleInactivityPing = useCallback((data: { workId: number; pingNumber: number }) => {
        setInactivityPingDialogOpen(true);
        setInactivityPingNumber(data.pingNumber);
        if (work?.id) {
            inactivityPingStorage.save(work.id, { pingDialogOpen: true, pingNumber: data.pingNumber });
        }
    }, [ work?.id ]);

    const handleCloseInactivityPingDialog = useCallback(() => {
        setInactivityPingDialogOpen(false);
        if (work?.id) {
            inactivityPingStorage.save(work.id, { pingDialogOpen: false, pingNumber: inactivityPingNumber });
        }
    }, [ work?.id, inactivityPingNumber ]);

    // WebSocket connection for work checkpoint updates
    useWorkCheckpointWebSocket({
        workActive,
        workId: work?.id,
        workToken,
        onSolutionUrlsUpdate: handleSolutionUrlsUpdate,
        onReviewStatusUpdate: handleReviewStatusUpdate,
        onConnectionStatusChange: handleConnectionStatusChange,
        onWorkflowsUpdate: handleWorkflowsUpdate,
        onInactivityPing: handleInactivityPing
    });

    const resetWorkState = useCallback((workId: number) => {
        setCurrentStep(ContributorWorkSteps.TaskAccept);
        setFeedbackAdded(false);
        setSolutionUrls([]);
        setQuestReviewFeedback('');
        setQuestReviewDialogOpen(false);
        setApproveDialogOpen(false);
        setApproveAttachmentIds([]);
        setAttachmentIds([]);
        setWorkflowsPassed(undefined);
        setWorkflowsFeedback(undefined);
        setIsAnalyzingWorkflows(false);
        clearAllWorkStorage(workId);
    }, []);

    const handleCancelWork = useCallback(
        () => {
            const durationSeconds = _calculateDurationSeconds();

            // checkpoint work before cancelling
            dispatch(
                checkpointWork({
                    workId: work.id,
                    durationSeconds
                })
            ).then(() => {
                // cancel work without waiting for checkpoint to be done
                dispatch(
                    cancelWork({
                        workId: work.id,
                        durationSeconds
                    })
                ).unwrap().then(() => {
                    setCancellationDialogOpen(false);
                    resetWorkState(work.id);
                });
            });
        },
        [
            _calculateDurationSeconds,
            cancelWork,
            checkpointWork,
            dispatch,
            resetWorkState,
            work
        ]
    );

    const handleReportBlocker = useCallback(
        () => {
            setBlockerDialogOpen(true);
        },
        []
    );

    const handleBlockerDialogClose = useCallback(
        () => {
            setBlockerDialogOpen(false);
            setBlockerDescription('');
        },
        []
    );

    const handleBlockerSubmit = useCallback(
        (blockerAttachmentIds: number[]) => {
            const durationSeconds = _calculateDurationSeconds();

            // checkpoint work before cancelling
            dispatch(
                checkpointWork({
                    workId: work.id,
                    durationSeconds
                })
            ).then(() => {
                // cancel work with blocker description and attachments
                dispatch(
                    cancelWork({
                        workId: work.id,
                        durationSeconds,
                        blockerDescription: blockerDescription,
                        attachmentList: blockerAttachmentIds
                    })
                ).unwrap().then(() => {
                    clearAllWorkStorage(work.id);
                    setBlockerDialogOpen(false);
                    setBlockerDescription('');
                    setFeedbackAdded(false);
                    setSolutionUrls([]);
                    setQuestReviewFeedback('');
                    setQuestReviewDialogOpen(false);
                    setApproveDialogOpen(false);
                    setApproveAttachmentIds([]);
                    setAttachmentIds([]);
                    setWorkflowsPassed(undefined);
                    setWorkflowsFeedback(undefined);
                    setIsAnalyzingWorkflows(false);
                    setCurrentStep(ContributorWorkSteps.TaskAccept);
                    // Load a new available work task
                    dispatch(loadAvailableWork({})).then(() => {
                        history.push('/');
                    });
                });
            });
        },
        [
            _calculateDurationSeconds,
            cancelWork,
            checkpointWork,
            dispatch,
            work,
            blockerDescription,
            history,
            loadAvailableWork
        ]
    );

    const handleSubmitWorkSolution = async (
        reviewStatus?: number, reviewFeedback?: string, solutionUrls?: string[], attachmentList?: number[], solveByReviewer?: boolean
    ) => {
        const durationSeconds = _calculateDurationSeconds();

        try {
            await dispatch(
                submitWorkSolution({
                    workId: work.id,
                    durationSeconds,
                    reviewStatus,
                    reviewFeedback,
                    solutionUrls,
                    attachmentList,
                    solveByReviewer
                })
            ).unwrap().then(async (result: { jobId?: string | null } | undefined) => {
                // clear all work storage
                clearAllWorkStorage(work.id);

                if (solveByReviewer === true) {
                    // "Fix by yourself" — redirect to the newly created work
                    dispatch(loadAvailableWork({})).then(() => {
                        setCurrentStep(ContributorWorkSteps.WorkingOnPullRequest);
                        setQuestReviewFeedback('');
                        setSolutionUrls([]); 
                        setFeedbackAdded(false); 
                    });
                    history.push('/');
                    return;
                }

                if (solveByReviewer === false) {
                    // "Back to breakdown" — smart breakdown runs in background, go to done
                    setCurrentStep(ContributorWorkSteps.Done);
                    setQuestReviewFeedback('');
                    setSolutionUrls([]);
                    setFeedbackAdded(false);
                    return;
                }

                // Default behavior for other flows
                setSolutionUrls([]);
                setFeedbackAdded(false);
                setQuestReviewFeedback('');

                // When the submitted PR had merge conflicts the backend resolves
                // them in an async job and returns its id. Keep the loader on
                // screen and poll until the job finishes.
                //
                // On success the backend leaves the work COMPLETE but the work
                // record ACTIVE and does NOT merge/finalize — it waits for the
                // finish to be re-submitted so the normal work-chain merges the
                // PRs and finalizes the task in one place. Re-submit that finish
                // automatically here (no contributor action), replaying the exact
                // same payload. Without this the conflicts get resolved but the
                // PR never merges and the work stays active forever.
                if (result?.jobId) {
                    setIsResolvingConflicts(true);
                    try {
                        const job = await pollAsyncJob(result.jobId);

                        if (job?.result?.status === 'resolved') {
                            await dispatch(
                                submitWorkSolution({
                                    workId: work.id,
                                    durationSeconds,
                                    reviewStatus,
                                    reviewFeedback,
                                    solutionUrls,
                                    attachmentList,
                                    solveByReviewer
                                })
                            ).unwrap();
                        }
                    } catch (error) {
                        console.error('Error resolving merge conflicts:', error);
                    } finally {
                        setIsResolvingConflicts(false);
                    }
                }

                setCurrentStep(ContributorWorkSteps.Done);
            });
        } catch (error) {
            console.error('Error submitting work solution:', error);
        }
    };

    const handleQuestReviewSubmit = (solveByReviewer: boolean) => {
        handleSubmitWorkSolution(
            WorkReviewStatus.INADEQUATE,
            questReviewFeedback,
            solutionUrls,
            attachmentIds,
            solveByReviewer
        );
        setQuestReviewDialogOpen(false);
        setQuestReviewFeedback('');
    };

    const handleReviewRequestRejectionBackToBreakdown = () => {
        handleSubmitWorkSolution(
            WorkReviewStatus.INADEQUATE,
            undefined,
            solutionUrls,
            attachmentIds,
            false // solveByReviewer = false means "Back to breakdown"
        );
    };

    const handleReviewRequestRejectionFixByYourself = () => {
        handleSubmitWorkSolution(
            WorkReviewStatus.INADEQUATE,
            undefined,
            solutionUrls,
            attachmentIds,
            true // solveByReviewer = true means "Fix by yourself"
        );
    };

    const handleApproveSubmit = useCallback(() => {
        // Submit approval with ratings, attachments, and ADEQUATE status
        // Note: solutionUrls should be undefined for approval (using original PR)
        handleSubmitWorkSolution(WorkReviewStatus.ADEQUATE, undefined, undefined, approveAttachmentIds);
        setApproveDialogOpen(false);
        setApproveAttachmentIds([]);
    }, [ approveAttachmentIds ]);

    const submitRatings = useCallback(
        (ratings: UserRatings, authorizationCode: string) => {
            return Promise.all(
                Object.keys(ratings).map((subject) => {
                    if (ratings[subject].score === 0) {
                        return Promise.reject();
                    }
                    return dispatch(
                        submitWorkRating({
                            code: authorizationCode,
                            subject: subject as RatingSubject,
                            rating: ratings[subject]
                        })
                    ).unwrap();
                })
            ).catch(function (err) {
                console.log('An error while rating work: ', err);
                dispatch(setWorkActionError('rating'));
            });
        },
        [ dispatch, submitWorkRating ]
    );

    const onSubmitRating = useCallback(
        (ratings: UserRatings) => {
            setWorkRatingSubmitted(true);
            if (!isRatingsCompleted(ratings)) {
                dispatch(setWorkActionError('rating_invalid'));
                return Promise.reject();
            }

            return submitRatings(ratings, workRatingAuthorizationCode)
                .catch(payload => {
                    console.log('error rating: ', payload);
                });
        },
        [ setWorkRatingSubmitted ]
    );

    const handleChatOpen = useCallback(() => {
        const entityId = workEntityId;
        const projectId = work.projectId;

        // If conversation already exists, open it and fetch messages
        if (workConversation && workConversation.conversationId) {
            const conversationId = workConversation.conversationId;
            
            // Open the chat
            dispatch(openChat({
                conversationId,
                entityId,
                projectId: projectId ?? null
            }));
            
            // Fetch user conversation state to update unread counts
            // ConversationContent will handle fetching messages when viewPageNumber is set
            if (entityId) {
                dispatch(fetchUserConversationState({
                    entityId,
                    resultsPerPage: CONVERSATION_PAGE_SIZE
                })).catch(() => {
                    // Ignore errors - ConversationContent will handle initial fetch
                });
            }
            
            return;
        }

        // Otherwise, create a new conversation
        const conversationName = `${lodash.startCase(WorkType[work.workType])} - ${work.title}`;
        dispatch(createNewConversation({
            name: conversationName,
            entityId,
            projectId: projectId ?? 0,
            projectName: work.projectName || ''
        })).unwrap().then((result) => {
            // Get the conversation ID from the createNewConversation response
            // The reducer already sets activeConversationId, but we need the ID here
            const conversationId = result.conversation?.id;
            if (conversationId) {
                dispatch(openChat({
                    conversationId,
                    entityId,
                    projectId: projectId ?? null
                }));
            }
        }).catch(() => {
            // If creation fails, try to open existing conversation if it exists
            if (workConversation && workConversation.conversationId) {
                const conversationId = workConversation.conversationId;
                dispatch(openChat({
                    conversationId,
                    entityId,
                    projectId: projectId ?? null
                }));
                
                // Fetch user conversation state - ConversationContent will handle fetching messages
                if (entityId) {
                    dispatch(fetchUserConversationState({
                        entityId,
                        resultsPerPage: CONVERSATION_PAGE_SIZE
                    })).catch(() => {
                        // Ignore errors - ConversationContent will handle initial fetch
                    });
                }
            }
        });

    }, [ dispatch, work, workEntityId, workConversation ]);

    // Handle openConversation parameter from URL
    useEffect(() => {
        if (openConversation && work) {
            handleChatOpen();
        }
    }, [ openConversation, work, handleChatOpen ]);

    if (isUserOffline(profile?.status)) {
        return <OfflineView />;
    }

    // PLANNING_QUESTIONS_ANSWERING short-circuits the standard work
    // view — it has no PR / attachments / review-status flow, just a
    // Q&A surface that mirrors the delegator's planning UI. Submit
    // triggers the same finish-work dispatch; the backend handler
    // (PlanningQuestionsAnsweringWorkFinishHandler) reads the linked
    // session_id from work_input and calls finalize_planning_session.
    if (isPlanningQuestionsAnsweringWorkItem(work.workType)) {
        if (currentStep === ContributorWorkSteps.Done) {
            return (
                <BG>
                    <Container>
                        <TaskCompleted
                            onNextTaskClicked={() => {
                                history.push('/');
                                dispatch(
                                    loadAvailableWork({ currentWorkId: work.id })
                                ).then(() => {
                                    setCurrentStep(
                                        ContributorWorkSteps.TaskAccept
                                    );
                                });
                            }}
                        />
                    </Container>
                </BG>
            );
        }
        const showPlanningQa =
            currentStep !== ContributorWorkSteps.TaskAccept;

        return (
            <BG>
                <Container>
                    <Header
                        currentStep={currentStep}
                        key={`header-${work.id}`}
                        name={work.title}
                        questionsOnClick={() =>
                            !workLoading && handleChatOpen()
                        }
                        workInProgressOnClick={() =>
                            !workLoading && setCancellationDialogOpen(true)
                        }
                        reportBlockerOnClick={() =>
                            !workLoading && handleReportBlocker()
                        }
                        workToken={workToken}
                        conversationNotification={hasUnreadMessages}
                        work={work}
                    />
                    {showPlanningQa ? (
                        <PlanningQuestionsAnswering
                            work={work}
                            onSubmitWork={() => {
                                void handleSubmitWorkSolution();
                            }}
                            isSubmittingWork={workLoading}
                        />
                    ) : (
                        <>
                            <Box height={15} />
                            <DescriptionTermsContainer>
                                <DescriptionWrapper>
                                    <Description
                                        key={`planning-work-description-${work.id}`}
                                        content={work.description}
                                        height="460px"
                                        title={getTaskDescriptionTitle(
                                            work.workType
                                        )}
                                        attachments={
                                            delegationAttachments.length > 0
                                                ? delegationAttachments
                                                : []
                                        }
                                    />
                                    {!workRatingSubmitted && (
                                        <DescriptionBox>
                                            <Rating
                                                ratings={workRatings}
                                                setRatings={setWorkRatings}
                                                onSubmit={onSubmitRating}
                                            />
                                        </DescriptionBox>
                                    )}
                                </DescriptionWrapper>
                                <Terms
                                    datetime={`${targetDate}`.replace(
                                        'T',
                                        ' '
                                    )}
                                    amount={null}
                                    variant={getVariant()}
                                    currentStep={currentStep}
                                />
                            </DescriptionTermsContainer>
                        </>
                    )}
                    <Dialogs
                        workId={work.id}
                        feedbackDialogOpen={feedbackDialogOpen}
                        cancellationDialogOpen={cancellationDialogOpen}
                        inactivityPingDialogOpen={inactivityPingDialogOpen}
                        inactivityPingNumber={inactivityPingNumber}
                        onSubmitFeedback={handleSubmitWorkFeedback}
                        onConfirmCancel={handleCancelWork}
                        onCloseFeedbackDialog={() => setFeedbackDialogOpen(false)}
                        onCloseCancellationDialog={() => setCancellationDialogOpen(false)}
                        onCloseInactivityPingDialog={handleCloseInactivityPingDialog}
                    />
                </Container>
            </BG>
        );
    }

    return (
        <BG>
            <Container>
                {isResolvingConflicts && (
                    <PlanningSpinner text="Submitting your solution and resolving merge conflicts. This may take a moment…" />
                )}
                {!isResolvingConflicts && currentStep === ContributorWorkSteps.Done && <TaskCompleted onNextTaskClicked={() => {
                    // Navigate to home page incase we aren't there
                    history.push('/');

                    // Load available work to clear the active work state
                    dispatch(loadAvailableWork({ currentWorkId: work.id })).then(() => {
                        setCurrentStep(ContributorWorkSteps.TaskAccept);
                        setQuestReviewFeedback('');
                    });
                }
                } />}
                {(!isResolvingConflicts && currentStep !== ContributorWorkSteps.Done) && (
                    <>
                        <Header
                            currentStep={currentStep}
                            key={`header-${work.id}`}
                            name={work.title}
                            questionsOnClick={() => !workLoading && handleChatOpen()}
                            workInProgressOnClick={() => !workLoading && setCancellationDialogOpen(true)}
                            reportBlockerOnClick={() => !workLoading && handleReportBlocker()}
                            workToken={workToken}
                            conversationNotification={hasUnreadMessages}
                            work={work}
                        />
                        <>
                            <Box height={15} />
                        </>
                        <DescriptionTermsContainer>
                            {
                                <DescriptionWrapper>
                                    <Description
                                        key={hasAssociatedBlocker ? `blocker-description-${work.id}` : `work-description-${work.id}`}
                                        content={work.description}
                                        height={
                                            isRequestReviewFeedbackMode
                                                ? '204px'
                                                : (
                                                    !hasAssociatedBlocker && (
                                                        (isFollowupWorkItem(work.workType) || isReviewRequestRejectionWorkItem(work.workType))
                                                    )
                                                        ? '408px'
                                                        : (
                                                            (currentStep === ContributorWorkSteps.SubmitPullRequest ||
                                                            currentStep === ContributorWorkSteps.DescriptionFeedback)
                                                                ? '230px'
                                                                : (
                                                                    currentStep === ContributorWorkSteps.WorkingOnPullRequest
                                                                        ? '408px'
                                                                        : '460px'
                                                                )
                                                        )
                                                )
                                        }
                                   
                                        title={hasAssociatedBlocker ? 'Blocker Description' : getTaskDescriptionTitle(work.workType)}
                                        attachments={originalQuestAttachments.length > 0 ? originalQuestAttachments : delegationAttachments}
                                        successCriteria={isQuestReviewWorkItem(work.workType) ? work.workInput?.original_quest_success_criteria : undefined}
                                    />
                                    {(currentStep !== ContributorWorkSteps.TaskAccept && isQuestReviewWorkItem(work.workType)) && (
                                        <>
                                            {hasClientReviewNotes && (
                                                <DescriptionBox>
                                                    <Description
                                                        key={`client-review-notes-${work.id}`}
                                                        content={clientReviewNotes}
                                                        height="204px"
                                                        title="Client Review Notes"
                                                        attachments={clientReviewAttachments}
                                                    />
                                                </DescriptionBox>
                                            )}
                                            {work.workInput?.['implementation_plan'] && (
                                                <DescriptionBox>
                                                    <ImplementationPlan
                                                        plan={work.workInput['implementation_plan']}
                                                    />
                                                </DescriptionBox>   
                                            )}
                                            {work.workInput?.workflows_feedback && 
                                             work.workInput?.workflows_passed === false && (
                                                <DescriptionBox>
                                                    <WorkflowStatus
                                                        workflowsPassed={false}
                                                        workflowsFeedback={work.workInput.workflows_feedback}
                                                        isAnalyzing={false}
                                                    />
                                                </DescriptionBox>
                                            )}
                                            <DescriptionBox>
                                                <QuestReviewProofOfWork
                                                    completedTasksWithAttachments={questReviewCompletedTasks}
                                                />
                                            </DescriptionBox>
                                        </>
                                    )}
                                    {isFollowupWorkItem(work.workType) && solutionAttachments.length > 0 && (
                                        <DescriptionBox>
                                            <SolutionAttachments attachments={solutionAttachments} />
                                        </DescriptionBox>)}
                                    {hasAssociatedBlocker && (
                                        <Description
                                            key={`blocker-task-${work.id}`}
                                            content={work.workInput['associated_blocker_task_description'] || ''}
                                            height={'204px'}
                                            title={work.workInput['associated_blocker_task_title'] || ''}
                                            attachments={blockerTaskAttachments}
                                        />
                                    )}
                                    {isQuestFeedbackHistoryMode && (
                                        <FeedbackHistory
                                            key={`quest-feedback-history-${work.id}`}
                                            history={work.workInput['quest_feedback_history']}
                                        />
                                    )
                                    }
                                    {(currentStep === ContributorWorkSteps.TaskAccept) && !workRatingSubmitted && (
                                        <DescriptionBox>
                                            <Rating
                                                ratings={workRatings}
                                                setRatings={setWorkRatings}
                                                onSubmit={onSubmitRating} />
                                        </DescriptionBox>
                                    )}

                                    {shouldShowPullRequestSection && (
                                        <>
                                            <DescriptionBox>
                                                <AddPullRequest 
                                                    onChange={setSolutionUrls}
                                                    value={solutionUrls}
                                                    resetKey={activeWorkRecord?.id ?? work.id}
                                                    workId={work.id}
                                                />
                                            </DescriptionBox>

                                            {workflowsRequired && solutionUrls?.length > 0 && (isAnalyzingWorkflows || workflowsPassed !== undefined) && (
                                                <DescriptionBox>
                                                    <WorkflowStatus
                                                        workflowsPassed={workflowsPassed}
                                                        workflowsFeedback={workflowsFeedback}
                                                        isAnalyzing={isAnalyzingWorkflows}
                                                    />
                                                </DescriptionBox>
                                            )}
                                            
                                            {shouldShowAttachmentsSection && (
                                                <DescriptionBox>
                                                    <AttachmentUpload
                                                        key={`attachment-${activeWorkRecord?.id ?? work.id}`}
                                                        attachmentIds={attachmentIds}
                                                        setAttachmentIds={setAttachmentIds}
                                                        onUploadingChange={setIsAttachmentUploading}
                                                        resetKey={activeWorkRecord?.id ?? work.id}
                                                    />
                                                </DescriptionBox>
                                            )}
                                        </>
                                    )}
                                </DescriptionWrapper>
                            }
                            <Terms
                                datetime={`${targetDate} ${currentStep !== ContributorWorkSteps.TaskAccept ? timeRemaining : ''}`.replace('T', ' ')}
                                amount={null}
                                variant={getVariant()}
                                currentStep={currentStep}
                            />
                        </DescriptionTermsContainer>
                        <Box height={15} />
                    </>
                )}
                <Box height={15} />
                {(currentStep !== ContributorWorkSteps.TaskAccept &&
                  activeWorkRecord) &&
                  !isQuestReviewWorkItem(work.workType) && (
                    <FooterButtons
                        onClickPrimary={() => handlePrimaryClick()}
                        primaryButton={getPrimaryText()}
                        onClickSecondary={() => handleSecondaryClick()}
                        primaryDisabled={getPrimaryDisabled()}
                        secondaryButton={getSecondaryText()}
                        secondaryDisabled={getSecondaryDisabled()}
                    />
                )}
                {/* Quest Review Dialog */}
                <QuestReviewDialog
                    open={questReviewDialogOpen}
                    questReviewFeedback={questReviewFeedback}
                    onQuestReviewFeedbackChange={setQuestReviewFeedback}
                    onSubmit={handleQuestReviewSubmit}
                />
                {/* Approve Dialog */}
                <ApproveDialog
                    open={approveDialogOpen}
                    attachmentIds={approveAttachmentIds}
                    setAttachmentIds={setApproveAttachmentIds}
                    onSubmit={handleApproveSubmit}
                    instructions={isQuestReviewWorkItem(work.workType) 
                        ? 'Please upload a video (or, if that\'s not possible, at least an image) that demonstrates that the quest was completed and meets the requirements and the success criteria. If your work is intended to run on multiple devices, make sure to show how it works on each of them.'
                        : undefined
                    }

                />
                {/* Blocker Dialog */}
                <BlockerDialog
                    open={blockerDialogOpen}
                    onClose={handleBlockerDialogClose}
                    blockerDescription={blockerDescription}
                    onBlockerDescriptionChange={setBlockerDescription}
                    onSubmit={handleBlockerSubmit}
                />
                <SelectModal
                    isVisible={isVisible}
                    onSave={handleSelectWorkTypeSubmit}
                    onCancel={handleClose}
                    options={options}
                />
                <Dialogs
                    workId={work.id}
                    feedbackDialogOpen={feedbackDialogOpen}
                    cancellationDialogOpen={cancellationDialogOpen}
                    inactivityPingDialogOpen={inactivityPingDialogOpen}
                    inactivityPingNumber={inactivityPingNumber}
                    onSubmitFeedback={handleSubmitWorkFeedback}
                    onConfirmCancel={handleCancelWork}
                    onCloseFeedbackDialog={() => setFeedbackDialogOpen(false)}
                    onCloseCancellationDialog={() => setCancellationDialogOpen(false)}
                    onCloseInactivityPingDialog={handleCloseInactivityPingDialog}
                />
                {workActionError && (
                    <Snackbar
                        open={!!workActionError}
                        autoHideDuration={6000}
                        onClose={() => dispatch(clearWorkActionError())}
                        message={formatErrorMessage(workActionError)}
                    />
                )}

                <Snackbar
                    open={workActive && wsConnected === false}
                    message="Connection to the service was lost, attempting reconnect..."
                />

                <WorkDialog
                    open={unavailableWorkDialog}
                    title="Task Canceled"
                    description="Please note that this task was canceled. You should stop working on it and move on to another task."
                    showCloseButton={false}
                    buttons={[
                        {
                            label: 'Confirm',
                            onClick: () => {
                                setUnavailableWorkDialog(false);
                                if (work?.id) {
                                    resetWorkState(work.id);
                                }
                                dispatch(loadAvailableWork({}));
                            }
                        }
                    ]}
                />
                <WorkDialog
                    open={inactivityPingDialogOpen}
                    title={'Inactivity Warning'}
                    description={[
                        'No activity has been detected for a while.',
                        'If you are still working on this task, please proceed shortly via the extension, or the task will be automatically cancelled.'
                    ]}
                    buttons={[
                        { label: 'Close', onClick: handleCloseInactivityPingDialog }
                    ]}
                />
            </Container>
        </BG>
    );
};

export default ContributorWork;
